import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { InfoHint } from '../../components/InfoHint';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, isValidGtin, PRODUCT_KIND_LABELS, PRODUCT_KINDS, TAX_RATES, type Product, type ProductKind, type TaxRate } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { centsText, parseCents } from '../../money';
import { ContactPicker, type PickedContact } from '../../components/ContactPicker';
import { toneClass } from '../../components/color/ColorSwatches';
import { useSession } from '../../session';
import { cmText, kgText, useProductLookups } from './lookups';
import './products.css';

/** „1,25“ → 1250 (Faktor 1000 für kg → g, 10 für cm → mm). Leer ergibt null, Unsinn undefined. */
function parseScaled(text: string, factor: number): number | null | undefined {
  const t = text.trim().replace(/\s/g, '').replace(',', '.');
  if (!t) return null;
  if (!/^\d*(\.\d*)?$/.test(t) || t === '.') return undefined;
  return Math.round(Number(t) * factor);
}

interface Draft {
  name: string;
  sku: string;
  kind: ProductKind;
  categoryId: string;
  unitId: string;
  bookingGroupId: string;
  description: string;
  price: string;
  taxRate: TaxRate;
  purchasePrice: string;
  supplier: PickedContact | null;
  supplierSku: string;
  ean: string;
  manufacturer: string;
  manufacturerSku: string;
  weight: string;
  length: string;
  width: string;
  height: string;
  note: string;
  isActive: boolean;
  isSellable: boolean;
  tagIds: string[];
}

const blank: Draft = {
  name: '', sku: '', kind: 'goods', categoryId: '', unitId: '', bookingGroupId: '', description: '', price: '', taxRate: 19, purchasePrice: '', supplier: null, supplierSku: '', ean: '',
  manufacturer: '', manufacturerSku: '', weight: '', length: '', width: '', height: '', note: '', isActive: true, isSellable: true, tagIds: [],
};

/**
 * Produkt anlegen oder bearbeiten (KON-108). Das Formular ist in Abschnitte gegliedert, nur die Bezeichnung ist Pflicht. Preise in Euro, Gewicht in kg und Maße
 * in cm, gespeichert werden Cent, Gramm und Millimeter. Bilder lädt man in der Ansicht des Produkts hoch.
 */
export function ProductFormPage() {
  const { id } = useParams();
  const isCreate = !id;
  const navigate = useNavigate();
  const client = useQueryClient();
  const { can } = useSession();
  const lookups = useProductLookups();
  const existing = useQuery({ queryKey: ['product', id], queryFn: async () => (await api<{ data: Product }>('GET', `/products/${id}`)).data, enabled: !isCreate });

  const [d, setD] = useState<Draft>(blank);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const loaded = useRef(false);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setD((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    const p = existing.data;
    if (loaded.current || !p) return;
    loaded.current = true;
    setD({
      name: p.name, sku: p.sku ?? '', kind: p.kind, categoryId: p.categoryId ?? '', unitId: p.unitId ?? '', bookingGroupId: p.bookingGroupId ?? '', description: p.description ?? '', price: centsText(p.salePriceCents),
      taxRate: p.taxRate, purchasePrice: p.purchasePriceCents === null ? '' : centsText(p.purchasePriceCents), supplier: p.supplierId ? { id: p.supplierId, name: p.supplierName ?? 'Lieferant' } : null,
      supplierSku: p.supplierSku ?? '', ean: p.ean ?? '', manufacturer: p.manufacturer ?? '', manufacturerSku: p.manufacturerSku ?? '', weight: kgText(p.weightGrams), length: cmText(p.lengthMm),
      width: cmText(p.widthMm), height: cmText(p.heightMm), note: p.note ?? '', isActive: p.isActive, isSellable: p.isSellable, tagIds: p.tagIds,
    });
  }, [existing.data]);
  // Neue Produkte bekommen „Stück“ als Einheit, sobald die Einheiten geladen sind
  useEffect(() => {
    if (isCreate && !d.unitId && lookups.units[0]) set('unitId', lookups.units[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, lookups.units]);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => (isCreate ? api<{ data: Product }>('POST', '/products', body) : api<{ data: Product }>('PATCH', `/products/${id}`, body)),
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['products'] });
      client.setQueryData(['product', res.data.id], res.data);
      navigate(`/produkte/${res.data.id}`, { replace: true });
    },
    onError: (e) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setFormError('Bitte prüfen Sie die markierten Felder.');
      } else setFormError(e instanceof ApiError ? e.message : 'Das Produkt konnte nicht gespeichert werden.');
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    const found: Record<string, string> = {};
    const price = d.price.trim() ? parseCents(d.price) : 0;
    const purchase = d.purchasePrice.trim() ? parseCents(d.purchasePrice) : null;
    const weight = parseScaled(d.weight, 1000);
    const dims = { lengthMm: parseScaled(d.length, 10), widthMm: parseScaled(d.width, 10), heightMm: parseScaled(d.height, 10) };
    if (d.name.trim().length < 2) found.name = 'Bezeichnung fehlt';
    if (price === null || price < 0) found.salePriceCents = 'Bitte einen Preis wie 24,90 angeben.';
    if (purchase !== null && purchase < 0) found.purchasePriceCents = 'Bitte einen Preis wie 14,00 angeben.';
    if (d.purchasePrice.trim() && purchase === null) found.purchasePriceCents = 'Bitte einen Preis wie 14,00 angeben.';
    if (d.ean.trim() && !isValidGtin(d.ean.replace(/\s+/g, ''))) found.ean = 'Ungültige EAN/GTIN. Sie hat 8, 12, 13 oder 14 Ziffern, die letzte ist eine Prüfziffer.';
    if (weight === undefined) found.weightGrams = 'Bitte eine Zahl wie 12,5 angeben.';
    for (const [k, v] of Object.entries(dims)) if (v === undefined) found[k] = 'Bitte eine Zahl wie 65 angeben.';
    setErrors(found);
    if (Object.keys(found).length) {
      setFormError('Bitte prüfen Sie die markierten Felder.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    save.mutate({
      name: d.name.trim(),
      sku: d.sku.trim() || null,
      kind: d.kind,
      categoryId: d.categoryId || null,
      unitId: d.unitId || null,
      bookingGroupId: d.bookingGroupId || null,
      description: d.description.trim() || null,
      salePriceCents: price,
      taxRate: d.taxRate,
      purchasePriceCents: purchase,
      supplierId: d.supplier?.id ?? null,
      supplierSku: d.supplierSku.trim() || null,
      ean: d.ean.trim() || null,
      manufacturer: d.manufacturer.trim() || null,
      manufacturerSku: d.manufacturerSku.trim() || null,
      weightGrams: weight,
      ...dims,
      note: d.note.trim() || null,
      isActive: d.isActive,
      isSellable: d.isSellable,
      tagIds: d.tagIds,
    });
  }

  if (!isCreate && existing.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!isCreate && (existing.isError || !existing.data)) {
    return (
      <div className="card narrow">
        <h1>Produkt nicht gefunden</h1>
        <Link to="/produkte">Zur Liste</Link>
      </div>
    );
  }

  const back = isCreate ? '/produkte' : `/produkte/${id}`;
  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  const text = (key: keyof Draft, label: ReactNode, props: { max?: number; placeholder?: string; errorKey?: string; hint?: string; inputMode?: 'decimal' | 'numeric' } = {}) => (
    <label className="field">
      <span>{label}</span>
      <input className={`input${props.inputMode ? ' num' : ''}`} value={d[key] as string} onChange={(e) => set(key, e.target.value as never)} maxLength={props.max} placeholder={props.placeholder} inputMode={props.inputMode} aria-invalid={!!errors[props.errorKey ?? key]} />
      {err(props.errorKey ?? key) ?? (props.hint ? <small className="muted">{props.hint}</small> : null)}
    </label>
  );
  const sale = parseCents(d.price || '0');
  const purchase = d.purchasePrice.trim() ? parseCents(d.purchasePrice) : null;
  const margin = sale !== null && purchase !== null && sale > 0 ? sale - purchase : null;
  const mains = lookups.categories.filter((c) => !c.parentId);

  return (
    <>
      <p><Link to={back} className="back-link">← {isCreate ? 'Alle Produkte' : 'Zum Produkt'}</Link></p>
      <div className="page-head"><h1>{isCreate ? 'Neues Produkt' : `${existing.data!.name} bearbeiten`}</h1></div>

      <form className="card product-form" onSubmit={submit} noValidate>
        {formError && <div className="alert alert--error" role="alert">{formError}</div>}

        <h2>Allgemein</h2>
        <div className="form-grid">
          {text('name', <>Bezeichnung <abbr title="Pflichtfeld">*</abbr></>, { max: 200 })}
          {text('sku', 'Artikelnummer', { max: 60, hint: 'Ihre eigene Nummer, je Produkt eindeutig.' })}
          <label className="field">
            <span>Art</span>
            <select className="input" value={d.kind} onChange={(e) => set('kind', e.target.value as ProductKind)}>
              {PRODUCT_KINDS.map((k) => <option key={k} value={k}>{PRODUCT_KIND_LABELS[k]}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Kategorie</span>
            <select className="input" value={d.categoryId} onChange={(e) => set('categoryId', e.target.value)} aria-invalid={!!errors.categoryId}>
              <option value="">Keine</option>
              {mains.map((m) => {
                const subs = lookups.categories.filter((c) => c.parentId === m.id);
                return subs.length ? (
                  <optgroup key={m.id} label={m.name}>
                    <option value={m.id}>{m.name} (allgemein)</option>
                    {subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </optgroup>
                ) : (
                  <option key={m.id} value={m.id}>{m.name}</option>
                );
              })}
            </select>
            {err('categoryId') ?? (lookups.categories.length === 0 && can('setup.write') ? <small className="muted">Kategorien legen Sie in der <Link to="/einrichtung/produktkategorien">Einrichtung</Link> an.</small> : null)}
          </label>
          <label className="field">
            <span>Einheit</span>
            <select className="input" value={d.unitId} onChange={(e) => set('unitId', e.target.value)} aria-invalid={!!errors.unitId}>
              <option value="">Keine</option>
              {lookups.units.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.symbol})</option>)}
            </select>
            {err('unitId')}
          </label>
          <label className="field">
            <span className="invoice-label">Buchungsgruppe<InfoHint label="Buchungsgruppe">Bestimmt das Sachkonto, auf das der Erlös gebucht wird, wenn dieses Produkt auf einer Rechnung steht. Ohne Gruppe richtet sich das Konto nach dem Steuerschlüssel. Für Gutscheine wählen Sie eine Gruppe der Art „Gutschein“. Die Gruppen und ihre Konten legen Sie mit Ihrem Steuerberater in der Einrichtung fest.</InfoHint></span>
            <select className="input" value={d.bookingGroupId} onChange={(e) => set('bookingGroupId', e.target.value)} aria-invalid={!!errors.bookingGroupId}>
              <option value="">Keine</option>
              {lookups.bookingGroups.map((g) => <option key={g.id} value={g.id}>{g.name}{g.accountNumber ? ` (${g.accountNumber})` : ' (ohne Konto)'}</option>)}
            </select>
            {err('bookingGroupId') ?? (lookups.bookingGroups.length === 0 && can('setup.write') ? <small className="muted">Buchungsgruppen legen Sie in der <Link to="/einrichtung/buchungsgruppen">Einrichtung</Link> an.</small> : null)}
          </label>
        </div>
        <label className="field">
          <span>Beschreibung</span>
          <textarea className="input textarea" rows={4} value={d.description} onChange={(e) => set('description', e.target.value)} maxLength={5000} placeholder="Erscheint im Angebot unter der Bezeichnung" />
          {err('description')}
        </label>
        {lookups.tags.length > 0 && (
          <div className="field">
            <span className="field__label">Tags</span>
            <div className="tag-picker" role="group" aria-label="Tags">
              {lookups.tags.map((t) => {
                const on = d.tagIds.includes(t.id);
                return (
                  <button key={t.id} type="button" className={`tag-toggle ${toneClass(t.color)}${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => set('tagIds', on ? d.tagIds.filter((x) => x !== t.id) : [...d.tagIds, t.id])}>
                    <span className="tag-toggle__mark" aria-hidden="true">{on ? '✓' : ''}</span>
                    {t.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <label className="check">
          <input type="checkbox" checked={d.isActive} onChange={(e) => set('isActive', e.target.checked)} />
          <span>Aktiv. Archivierte Produkte stehen im Angebot nicht zur Wahl.</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={d.isSellable} onChange={(e) => set('isSellable', e.target.checked)} />
          <span>Wird verkauft. Ohne Häkchen (Verbrauchsmaterial wie Schrauben oder Verpackung) erscheint das Produkt nur im Lager, nicht in Angeboten, Aufträgen und Rechnungen.</span>
        </label>

        {d.isSellable && (
          <>
        <h2>Verkauf</h2>
        <div className="form-grid">
          {text('price', 'Verkaufspreis netto €', { placeholder: '0,00', errorKey: 'salePriceCents', inputMode: 'decimal' })}
          <label className="field">
            <span>Steuersatz</span>
            <select className="input" value={d.taxRate} onChange={(e) => set('taxRate', Number(e.target.value) as TaxRate)}>
              {TAX_RATES.map((r) => <option key={r} value={r}>{r} %</option>)}
            </select>
          </label>
        </div>
          </>
        )}

        <h2>Einkauf</h2>
        <div className="form-grid">
          {text('purchasePrice', 'Einkaufspreis netto €', { placeholder: '0,00', errorKey: 'purchasePriceCents', inputMode: 'decimal', hint: margin !== null ? `Marge ${formatCents(margin)} (${Math.round((margin / sale!) * 100)} %)` : undefined })}
          <div className="field">
            <span className="field__label">Lieferant</span>
            <ContactPicker value={d.supplier} onChange={(c) => set('supplier', c)} invalid={!!errors.supplierId} label="Lieferant" />
            {err('supplierId')}
          </div>
          {text('supplierSku', 'Artikelnummer beim Lieferanten', { max: 60 })}
        </div>

        <h2>Kennzeichnung</h2>
        <div className="form-grid">
          {text('ean', 'EAN / GTIN', { max: 20, inputMode: 'numeric', placeholder: '4006381333931' })}
          {text('manufacturer', 'Hersteller', { max: 120 })}
          {text('manufacturerSku', 'Herstellernummer', { max: 60 })}
        </div>

        <h2>Abmessungen</h2>
        <div className="form-grid product-form__dims">
          {text('weight', 'Gewicht kg', { errorKey: 'weightGrams', inputMode: 'decimal', placeholder: '0,000' })}
          {text('length', 'Länge cm', { errorKey: 'lengthMm', inputMode: 'decimal' })}
          {text('width', 'Breite cm', { errorKey: 'widthMm', inputMode: 'decimal' })}
          {text('height', 'Höhe cm', { errorKey: 'heightMm', inputMode: 'decimal' })}
        </div>

        <h2>Interne Notiz</h2>
        <label className="field">
          <span className="sr-only">Interne Notiz</span>
          <textarea className="input textarea" rows={3} value={d.note} onChange={(e) => set('note', e.target.value)} maxLength={5000} placeholder="Nur für Ihr Team sichtbar" />
        </label>

        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : isCreate ? 'Produkt anlegen' : 'Speichern'}</button>
          <Link className="btn btn--secondary" to={back}>Abbrechen</Link>
        </div>
        {isCreate && <p className="muted small">Bilder laden Sie nach dem Anlegen in der Ansicht des Produkts hoch.</p>}
      </form>
    </>
  );
}
